import { Box } from "@twilio-paste/core/box";
import {
    DisclosurePrimitive,
    DisclosurePrimitiveContent,
    useDisclosurePrimitiveState
} from "@twilio-paste/core/disclosure-primitive";
import { graphql, useStaticQuery } from "gatsby";
import startCase from "lodash/startCase";
import toLower from "lodash/toLower";
import React from "react";
import { PagesQuery } from "../../../queries";
import { SidebarAnchor } from "./SidebarAnchor";
import { SidebarDisclosureButton } from "./SidebarDisclosureButton";
import { SidebarItem } from "./SidebarItem";
import { SidebarNestedItem } from "./SidebarNestedItem";
import { SidebarNestedList } from "./SidebarNestedList";
import { VersionSwitcher } from "./VersionSwitcher";

interface path {
    name: string;
    children: path[];
    context?: {
        docName: string | null;
    };
}

const replacements: Record<string, string> = {
    nsa: "Notifications, Sounds and Audio",
    "ui-actions": "UI Actions and Events",
    DynamicContentStore: "Add / Replace / Remove Methods",
    "context-providers": "Context Providers",
    FlexEvent: "Flex Events",
    Config: "Configuration",
    "flex-errors-and-logs": "Flex Errors and Logs",
    "flex-icons": "Flex Icons",
    "custom-icons": "Custom Icons"
};

const reverseSnakeCase = (string: string) => startCase(toLower(string.split("-").join(" ")));

const getPathName = () => {
    if (typeof window !== "undefined") {
        return window?.location?.pathname;
    }
    return "";
};

const transform = (paths: path, [head, ...tail]: string[]) => {
    let child = paths.children.find((c) => c.name === head);
    if (!child) paths.children.push((child = { name: head, children: [] }));
    if (tail.length > 0) transform(child, tail);
    return paths;
};

const SidebarNavigation = () => {
    const pages: PagesQuery[] = useStaticQuery(graphql`
        {
            pages: allSitePage(filter: { path: { regex: "/^((?!404|^/$).)*$/" } }) {
                nodes {
                    path
                    context {
                        docName
                    }
                }
            }
        }
    `).pages.nodes;
    const paths = pages
        .map((page: PagesQuery) => page.path.split("/").slice(1, -1))
        .reduce(transform, {
            name: "",
            children: []
        });

    const generateSideBar = ({ name, children, context }: path, basePath?: string) => {
        if (!children.length) {
            return (
                <SidebarNestedItem key={name}>
                    <SidebarAnchor nested to={`${basePath}/${name}/`}>
                        {context?.docName || replacements[name] || name}
                    </SidebarAnchor>
                </SidebarNestedItem>
            );
        }

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const disclosure = useDisclosurePrimitiveState({
            visible: !!getPathName()?.includes(name)
        });

        const component = (
            <>
                <DisclosurePrimitive as={SidebarDisclosureButton} {...disclosure} data-testid={`${name}-button`}>
                    {context?.docName || replacements[name] || reverseSnakeCase(name)}
                </DisclosurePrimitive>
                <DisclosurePrimitiveContent {...disclosure} data-testid={`${name}-list`}>
                    <SidebarNestedList>
                        {children.map((_) => generateSideBar(_, basePath ? `${basePath}/${name}` : `/${name}`))}
                    </SidebarNestedList>
                </DisclosurePrimitiveContent>
            </>
        );

        return basePath ? (
            <SidebarNestedItem key={name} paddingLeft="space30">
                {component}
            </SidebarNestedItem>
        ) : (
            <SidebarItem key={name}>{component}</SidebarItem>
        );
    };

    return (
        <Box
            as="nav"
            marginTop={["space0", "space0", "space70"]}
            marginLeft={["space10", "space10", "space0"]}
            paddingBottom={["space50", "space50", "space0"]}
            overflow="auto"
            role="navigation"
            aria-label="Main"
        >
            <VersionSwitcher />
            <Box as="ul" padding="space0" margin="space0" listStyleType="none">
                <SidebarItem>
                    <SidebarAnchor to="/">Home</SidebarAnchor>
                </SidebarItem>
                {paths.children.map((category: path) => generateSideBar(category))}
            </Box>
        </Box>
    );
};

export { SidebarNavigation };
